import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { AssetDto, GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  filter,
  from,
  map,
  of,
  race,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import {
  GALLERY_DOWNLOAD_SIZE,
  assetsPerResizeBatch,
  maxResizeBatchRetryCount,
  maxZipRequestSize,
  resizedImageDownloadFormatMap,
} from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { Archive, ArchiveRequest, ArchiveRequestGallery, ArchiveSize } from '../interfaces/archive-request.interface';
import { copy } from '../lib/copy';
import { openFile } from '../lib/download-file';
import { estimateResizedAssetSize } from '../lib/estimate-resized-asset-size';
import { md5 } from '../lib/md5';
import { randomString } from '../lib/random-string';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { ForkJoinWithConcurrencyLimit } from '../lib/throttled-fork-join';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { AppConfigService } from '../providers/config.service';
import { AssetService } from './asset.service';
import { GalleryPasswordService } from './gallery-password.service';
import { NetworkService } from './network.service';
import { ProgressToastService } from './progress-toast.service';
import { SocketService } from './socket.service';
import { ToastService } from './toast.service';
import { VirusScanService } from './virus-scan.service';

export interface ArchiveItem {
  archive: Archive;
  progress: number;
  resizeProgress: number;
  completed: boolean;
  // downloaded: boolean;
  errored: boolean;
  cancelled: boolean;
  assetCount: number;
  assetResizingDoneCount: number;
  key: string;
  partInfo?: { index: number; total: number };
}

@Injectable({
  providedIn: 'root',
})
export class ArchiveService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private progressToastService = inject(ProgressToastService);
  private socketService = inject(SocketService);
  private assetService = inject(AssetService);
  private networkService = inject(NetworkService);
  private toasts = inject(ToastService);
  private growthBook = inject(GROWTHBOOK);
  public items = signal<ArchiveItem[]>([]);

  public onArchiveCreated$ = new Subject<ArchiveItem>();
  private onArchiveCreated = this.onArchiveCreated$.subscribe(archive => {
    this.items.set([...this.items(), archive]);
    this.progressToastService.addToast({
      id: archive.key,
      type: 'archive',
    });
  });

  public onArchiveFinalized$ = new Subject<Archive>();
  private onArchiveFinalized = this.onArchiveFinalized$.subscribe(archive => {
    const item = this.items().find(i => i.archive.id === archive.id);
    if (!item) return console.warn('Got info about archive that we are not watching');
    item.archive = archive;
    this.items.update(v => [...v]);
    this.onCompleted(item);
  });

  public onArchiveCancelled$ = new Subject<Archive>();

  public onArchiveFailed$ = new Subject<Archive>();
  private onArchiveFailed = this.onArchiveFailed$.subscribe(archive => {
    const item = this.items().find(i => i.archive.id === archive.id);
    if (!item) return console.warn('Got info about archive that we are not watching');
    item.progress = 0;
    item.errored = true;
    this.items.update(v => [...v]);
    return timer(5000).subscribe(() => {
      this.progressToastService.removeToast(item.key);
      this.items.set(this.items().filter(i => i !== item));
    });
  });

  public onArchiveProgress$ = new BehaviorSubject<ArchiveItem>(undefined);
  private onArchiveDownloaded$ = new Subject<ArchiveItem>();
  private onArchiveDownloaded = this.onArchiveDownloaded$.subscribe(item => {
    return timer(2500).subscribe(() => {
      this.progressToastService.removeToast(item.key);
      this.items.set(this.items().filter(i => i !== item));
    });
  });
  private analytics = inject(PICFLOW_ANALYTICS);
  private virusScanService = inject(VirusScanService);

  constructor() {
    this.socketService.onSocketEvent.subscribe(event => {
      if (!event?.payload) return;

      const archive = (event.payload.archive || event.payload.Archive) as Archive;

      if (event.name === 'archive.finalized') {
        this.onArchiveFinalized$.next(archive);
      }

      if (event.name === 'archive.finalized.fail') {
        this.onArchiveFailed$.next(archive);
      }

      if (event.name === 'archive.progress') {
        const payload = (event.payload.payload || event.payload.progress) as {
          // event.payload.payload can be removed after backend update
          id_archive: string;
          current: string;
          total: string;
        };

        const item = this.items().find(i => i.archive.id === payload.id_archive);
        if (!item) return console.warn('Got info about archive that we are not watching');
        const total = Number(BigInt(payload.total || payload.current) / 1024n);
        const current = Number(BigInt(payload.current) / 1024n);

        item.progress = (100 / total) * current;
        this.items.update(v => [...v]);
        this.onArchiveProgress$.next(item);
      }
    });
  }

  public downloadArchive(item: ArchiveItem) {
    openFile(`${this.config.filesEndpoint}/${item.archive.download_url}`, item.archive.zip_name + '.zip');
    this.onDownloaded(item);
  }

  onDownloaded(item: ArchiveItem) {
    this.analytics.track('archiveDownload', false, { size: parseInt(<string>(<unknown>item.archive.total_size)) });
    this.onArchiveDownloaded$.next(item);
  }

  onCompleted(item: ArchiveItem) {
    item.progress = 100;
    item.completed = true;
    this.items.update(v => [...v]);
    this.analytics.track('archiveCompleted', false, { size: parseInt(<string>(<unknown>item.archive.total_size)) });

    if (!item.cancelled) {
      this.downloadArchive(item);
    }
  }

  public cancelArchive(item: ArchiveItem) {
    this.onArchiveCancelled$.next(item.archive);
    this.progressToastService.removeToast(item.key);
    this.items.set(this.items().filter(i => i !== item));
  }

  public cancelAll() {
    this.items().forEach(item => {
      this.onArchiveCancelled$.next(item.archive);
    });
    this.items.set([]);
  }

  public requestArchiveCreation(
    params: ArchiveRequest,
    galleryId: string,
    assetCount: number,
    totalDownloadSize: bigint,
    partInfo?: { index: number; total: number }
  ): Observable<Archive> {
    const key = this.getZipKey(params);
    if (this.items().find(item => item.key === key)) return of(undefined);

    const zip_parameters = params.zip_parameters.map(copy);

    const tempArchive = this.addtoArchiveItems(
      <Archive>{
        zip_name: params.zip_name,
        zip_parameters,
        size: params.size,
        number_items: assetCount,
        status: params.size === 'original' ? 'pending' : 'resizing',
        total_size: totalDownloadSize.toString(),
      },
      assetCount,
      partInfo
    );

    return of(undefined).pipe(
      switchMap(() => {
        if (params.size === 'original') return of(undefined);
        const data = params.zip_parameters.reduce(
          (all, p) => [...all, ...(p.datas || [])],
          [] as { uuid: string; extension: string }[]
        );

        return this.resizeAssets(data, params.size, tempArchive).pipe(
          map(resp => {
            if (!resp) return throwError(() => 'Failed to resize assets');
            tempArchive.archive.status = 'pending';
            this.onArchiveProgress$.next(tempArchive);
          })
        );
      }),
      switchMap(() => {
        zip_parameters.forEach(p => {
          delete p['assetUuids'];
        });

        return this.http
          .post<Archive>(`${this.config.apiEndpoint}/archives${this.getPassword(galleryId)}`, params)
          .pipe(
            switchMap(archive => {
              if (!archive) return throwError(() => 'Failed to create archive');
              archive.total_size = archive.total_size || totalDownloadSize.toString();
              this.updateArchive(tempArchive, archive);
              return this.socketService
                .sendMessage('archive.join', {
                  id_archive: archive.id,
                })
                .pipe(map(() => archive));
            })
          );
      }),
      catchError(err => {
        this.onArchiveFailed$.next(tempArchive.archive);
        if (err !== 'Failed to resize assets') {
          return throwError(() => err);
        }
      })
    );
  }

  addtoArchiveItems(archive: Archive, assetCount: number, partInfo?: { index: number; total: number }) {
    const key = this.getZipKey(archive);
    const item: ArchiveItem = {
      archive,
      progress: 0,
      resizeProgress: 0,
      completed: false,
      errored: false,
      cancelled: false,
      assetCount: assetCount,
      assetResizingDoneCount: 0,
      key,
      partInfo,
    };
    this.onArchiveCreated$.next(item);
    return item;
  }

  updateArchive(item: ArchiveItem, archive: Archive) {
    item.archive = archive;
    this.items.update(v => [...v]);
    return timer(1000).subscribe(() => {
      if (item.completed) return;

      // Check if the archive was completed before we could even subscribe
      return this.getArchive(item.archive.id).subscribe(_archive => {
        if (_archive.status === 'completed') {
          this.onArchiveFinalized$.next(_archive);
        }
        return item;
      });
    });
  }

  private getArchive(id: string): Observable<Archive> {
    return this.http.get<Archive>(`${this.config.apiEndpoint}/archives/${id}`).pipe(
      catchError(err => {
        if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
        console.error(err);
        return of(undefined);
      })
    );
  }

  private createArchiveRequestGallery(
    gallery: GalleryDto,
    items: string[] | null,
    zip_dir: string,
    datas: { uuid: string; extension: string }[]
  ): ArchiveRequestGallery {
    if (items && items.length === gallery.assets_count) items = undefined;
    return { gallery: gallery.id, zip_dir, items, datas };
  }

  public createArchiveRequest(
    zipName: string,
    archiveRequestGalleries: ArchiveRequestGallery[],
    size: ArchiveSize
  ): ArchiveRequest {
    return {
      zip_name: zipName
        .trim()
        .split('\n')
        .join(' ')
        .replace(/[/\\#,+()$~%.'":*?<>{}]/g, ''),
      size,
      zip_parameters: archiveRequestGalleries.filter(r => !r.items || r.items.length),
    };
  }

  public getZipName(gallery: GalleryDto): string {
    if (gallery.name) return gallery.name;
    if (gallery.title) return gallery.title;
    if ((gallery['tenant'] as TenantDto)?.name) return 'Gallery by ' + (gallery['tenant'] as TenantDto).name;
    return 'Picflow Gallery';
  }

  public getMaxShortEdgeForGallery(galleryId: string): Observable<number> {
    return this.assetService.getAssetsForGalleryObservable(galleryId).pipe(
      take(1),
      map(assets => this.getMaxShortEdge(assets))
    );
  }

  public getMaxShortEdge(assets: AssetDto[]) {
    let maxShortEdge = 0;
    assets.forEach(asset => {
      const vData = asset.version_data;
      if (!vData.data) return;
      const shortEdge = vData.data.width < vData.data.height ? vData.data.width : vData.data.height;
      maxShortEdge = Math.max(maxShortEdge, shortEdge);
    });
    return maxShortEdge;
  }

  public onArchiveDone(archive: Archive) {
    if (!archive) return of(undefined);
    return race([
      this.onArchiveFinalized$.pipe(filter(archive => archive.status === 'completed' && archive.id === archive.id)),
      this.onArchiveCancelled$.pipe(filter(archive => archive.id === archive.id)),
      this.onArchiveFailed$.pipe(filter(archive => archive.id === archive.id)),
    ]).pipe(take(1));
  }

  public filterAssets(assets: AssetDto[], gallery: GalleryDto, size: ArchiveSize) {
    if (size !== 'original') {
      assets = assets.filter(a => {
        if (!a.version_data.data?.width) return false;
        if (a.type !== 'image') return false;
        return true;
      });
    }
    if (!gallery.watermark || size !== 'original') {
      return assets;
    }

    // originals
    return assets.filter(asset => !asset.watermark_enabled);
  }

  public createGalleryArchives(galleries: GalleryDto[], size: ArchiveSize) {
    return forkJoinSafe(
      galleries.map(gallery =>
        from(this.assetService.getAssetsForGallery(gallery.id, { sorted: true })).pipe(
          map(assets => ({ gallery, assets }))
        )
      )
    ).pipe(switchMap(galleryDatas => this.createGalleryArchivesWithAssets(galleryDatas, size, false)));
  }

  public createGalleryArchivesWithAssets(
    galleryDatas: {
      gallery: GalleryDto;
      assets: AssetDto[];
    }[],
    size: ArchiveSize,
    includeAllAssets: boolean
  ): Observable<
    {
      request: ArchiveRequest;
      assetCount: number;
      totalSize: bigint;
    }[]
  > {
    const assets = galleryDatas.map(data => data.assets).flat();
    return forkJoinSafe(assets.map(asset => this.virusScanService.onScanned(asset.version_data))).pipe(
      map(() => galleryDatas),
      catchError(err => {
        console.warn(err);
        this.toasts.add('Download Failed, virus detected', 'error');
        return of(undefined);
      }),
      map(galleryDatas => {
        if (!galleryDatas) return [];
        const zipName = this.getZipName(galleryDatas[0].gallery);
        const requestDatasList: {
          gallery: GalleryDto;
          datas: { uuid: string; extension: string }[];
          assetIds: string[];
          totalSize: bigint;
        }[][] = [];

        let requestDatas: {
          gallery: GalleryDto;
          datas: { uuid: string; extension: string }[];
          assetIds: string[];
          totalSize: bigint;
        }[] = [];

        galleryDatas.forEach(data => {
          let requestData: (typeof requestDatas)[number] = {
            gallery: data.gallery,
            datas: [],
            assetIds: [],
            totalSize: 0n,
          };
          requestDatas.push(requestData);
          data.assets
            .filter(asset => {
              const assetIsWatermarked = !!asset.version_data.watermark_uuid && asset.watermark_enabled;
              if (size === 'original') return !assetIsWatermarked;
              if (!asset.version_data.data?.width) return false;

              if (asset.type !== 'image') return false;

              return true;
            })
            .forEach(asset => {
              if (asset.version_data.size) {
                if (size === 'original') {
                  requestData.totalSize += BigInt(asset.version_data.size);
                } else {
                  const downloadSize = estimateResizedAssetSize(asset.version_data, GALLERY_DOWNLOAD_SIZE[size]);
                  requestData.totalSize += downloadSize;
                }
              }

              const uuid = asset.watermark_enabled
                ? asset.version_data.watermark_uuid || asset.version_data.uuid
                : asset.version_data.uuid;
              requestData.datas.push({ uuid, extension: asset.version_data.asset_name.split('.').pop().toLowerCase() });
              requestData.assetIds.push(asset.id);

              if (
                requestData.totalSize > this.growthBook.getFeatureValue('max-zip-size', maxZipRequestSize) &&
                requestData.assetIds.length > 0
              ) {
                requestDatasList.push(requestDatas);
                requestData = { gallery: data.gallery, datas: [], assetIds: [], totalSize: 0n };
                requestDatas = [requestData];
              }
            });
        });

        requestDatasList.push(requestDatas);

        const filteredRequestDatasList = requestDatasList.filter(a => a.filter(b => b.datas.length).length);

        const requests = filteredRequestDatasList
          .map((galleryDatas, index) => {
            const zipNameExtension =
              filteredRequestDatasList.length === 1 ? '' : ` ${index + 1} of ${filteredRequestDatasList.length}`;
            const request = this.createArchiveRequest(
              zipName + zipNameExtension,
              [
                ...galleryDatas.map(d =>
                  this.createArchiveRequestGallery(
                    d.gallery,
                    includeAllAssets ? undefined : d.assetIds, // undefined to automatically include all assets when we are not splitting the zip into multiple parts
                    `/${this.getZipName(d.gallery).replaceAll(/[/|":*<>]/g, '-')}`,
                    d.datas
                  )
                ),
              ],
              size
            );

            const assetCount = galleryDatas.reduce((t, g) => t + g.datas.length, 0);
            const totalSize = galleryDatas.reduce((t, g) => t + g.totalSize, 0n);

            return { request, assetCount, totalSize };
          })
          .filter(a => a);

        return requests;
      })
    );
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '?password=' + encodeURIComponent(password);
  }

  private getZipKey(assetDownload: ArchiveRequest) {
    return md5(
      JSON.stringify({
        size: assetDownload.size,
        zip_name: assetDownload.zip_name,
        zip_parameters: assetDownload.zip_parameters,
      })
    );
  }

  private resizeAssets(datas: { uuid: string; extension: string }[], size: ArchiveSize, archive: ArchiveItem) {
    const batches: { uuid: string; extension: string }[][] = [];

    const batchCount = Math.ceil(datas.length / assetsPerResizeBatch);

    for (let i = 0; i < batchCount; i++) {
      batches.push(datas.slice(i * assetsPerResizeBatch, (i + 1) * assetsPerResizeBatch));
    }

    if (!batches.length) return of([true]);

    return ForkJoinWithConcurrencyLimit(
      batches.map(batch =>
        this.resizeAssetsBatch(batch, size).pipe(
          tap(() => {
            archive.assetResizingDoneCount += batch.length;
            archive.resizeProgress = (100 / datas.length) * archive.assetResizingDoneCount;
            this.onArchiveProgress$.next(archive);
          })
        )
      ),
      1
    );
  }

  private resizeAssetsBatch(
    datas: { uuid: string; extension: string }[],
    size: ArchiveSize,
    retryCount = 0
  ): Observable<boolean> {
    return of(undefined).pipe(
      switchMap(() => {
        return forkJoinSafe(
          datas.map(data => this.resizeAsset(data.uuid, GALLERY_DOWNLOAD_SIZE[size], data.extension))
        ).pipe(
          map(() => true),
          catchError(err => {
            console.error(err);
            if (retryCount >= maxResizeBatchRetryCount) return of(false);
            return this.networkService.online$.pipe(
              switchMap(() => {
                return this.resizeAssetsBatch(datas, size, retryCount + 1);
              })
            );
          })
        );
      })
    );
  }

  private resizeAsset(uuid: string, size: number, extension: string) {
    const format = resizedImageDownloadFormatMap[extension.toLowerCase()] || 'jpg';
    const url = `${this.config.assetsEndpoint}/images/resized/s${size}/${uuid}.${format}?r=${randomString(12)}`; // the random string is because we need to avoid the cache and make sure that the resized image really exists in S3
    return from(fetch(url, { method: 'HEAD' }));
  }
}
